import React, { useState, useRef } from 'react'
import clsx from 'clsx'
import DeleteIcon from '@/shared/icons/DeleteIcon'
import ButtonIcon from '@/shared/components/UI/ButtonIcon'
import MinusIcon from '@/shared/icons/MinusIcon'
import PlusIcon from '@/shared/icons/PlusIcon'
import EyeIcon from '@/shared/icons/EyeIcon'
import CloseEyeIcon from '@/shared/icons/CloseEyeIcon'

const Input = ({
  label,
  placeholder,
  value,
  onChange,
  helperText,
  error,
  icon,
  iconPlacement,
  showDeleteButton = false,
  onDelete,
  className = '',
  inputClassName = '',
  labelClassName = '',
  wrapperClassName = '',
  canHaveError = true,
  type = 'text',
  min,
  max,
  step,
  onEnter,
  ...props
}) => {
  const [showPassword, setShowPassword] = useState(false)
  const inputRef = useRef(null)
  const hasError = !!error 
  const hasHelperText = !!helperText && !hasError
  const displayError = error
  const isNumberType = type === 'number'
  const isPasswordType = type === 'password'

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev)
  }

  const handleIncrement = () => {
    if (inputRef.current) {
      inputRef.current.stepUp()
      const event = new Event('input', { bubbles: true })
      inputRef.current.dispatchEvent(event)
    }
  }

  const handleDecrement = () => {
    if (inputRef.current) {
      inputRef.current.stepDown()
      const event = new Event('input', { bubbles: true })
      inputRef.current.dispatchEvent(event)
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      onEnter()
    }
  }

  return (
    <div
      className={clsx(
        'flex',
        'w-full',
        'flex-col',
        'items-start',
        wrapperClassName
      )}
    >
        {/* Label and Input Wrapper */}
        <div
          className={clsx(
            'flex',
            'flex-col',
            'items-start',
            'gap-v3-xs',
            'self-stretch',
            'flex-1'
          )}
        >
          {/* Label */}
          {label && (
            <label
              className={clsx(
                'text-v3-black-max',
                'text-v3-h10',
                'font-[510]',
                'not-italic',
                labelClassName
              )}
            >
              {label}
            </label>
          )}

          {/* Input Container with Icon Support */}
          <div className={clsx(
            'relative', 
            'w-full', 
            'self-stretch', 
            'flex items-center', 
            'gap-[12px]',
            isNumberType && 'group'
          )}>
            {icon && iconPlacement === 'pre' && (
              <div className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center pointer-events-none z-10">
                {icon}
              </div>
            )}
            <input
              ref={isNumberType ? inputRef : undefined}
              type={isPasswordType ? (showPassword ? 'text' : 'password') : type}
              value={value}
              onChange={onChange}
              placeholder={placeholder}
              min={min}
              max={max}
              step={step}
              className={clsx(
                'flex',
                'h-10',
                'py-v3-xs',
                icon && iconPlacement === 'pre' ? 'pl-9 pr-3' : '',
                icon && iconPlacement === 'post' ? 'pl-3 pr-9' : '',
                isNumberType && (showDeleteButton ? 'pl-3 pr-20' : 'pl-3 pr-20'),
                !icon && !isNumberType && 'px-3',
                isPasswordType && (showDeleteButton ? 'pr-20' : 'pr-10'),
                isNumberType && '[appearance:textfield]',
                isNumberType && '[&::-webkit-outer-spin-button]:appearance-none',
                isNumberType && '[&::-webkit-inner-spin-button]:appearance-none',
                'items-start',
                'self-stretch',
                'w-full',
                'rounded-v3-xs',
                'border',
                hasError ? 'border-v3-red-dark' : 'border-v3-black-inactive',
                'bg-white',
                'text-v3-h8',
                'text-v3-black-max',
                'placeholder:text-v3-grey-placeholder',
                'font-normal',
              //   hasError ? 'focus:ring-v3-red-primary' : 'focus:ring-v3-purple-primary',
              //   'focus:border-transparent',
                'transition-all',
                inputClassName
              )}
              onKeyDown={handleKeyDown}
              {...props}
            />
            {/* Number Control Buttons - Inside input on right */}
            {isNumberType && (
              <div 
                className={clsx(
                  'absolute',
                  'right-3',
                  'top-1/2',
                  '-translate-y-1/2',
                  'flex',
                  'flex-row',
                  'items-center',
                  'z-10',
                  'gap-v3-xxs'
                )}
              >
                <button
                  onClick={handleDecrement}
                  size="xs"
                >
                  <MinusIcon className='text-v3-purple-primary hover:text-v3-purple-medium' />
                </button>
                <button
                  onClick={handleIncrement}
                  size="xs"
                >
                  <PlusIcon className='text-v3-purple-primary hover:text-v3-purple-medium' />
                </button>
              </div>
            )}
            {/* Password Toggle Button */}
            {isPasswordType && (
              <div className={clsx(
                "absolute top-1/2 -translate-y-1/2 flex items-center",
                showDeleteButton ? 'right-12' : 'right-3'
              )}>
                <ButtonIcon
                  icon={showPassword ? CloseEyeIcon : EyeIcon}
                  onClick={togglePasswordVisibility}
                  size="sm"
                />
              </div>
            )}
            {/* Delete Button */}
            {showDeleteButton && !isNumberType && (
              <div className={clsx(
                "flex items-center",
                isPasswordType ? 'right-12' : ''
              )}>
                <ButtonIcon
                  icon={<DeleteIcon className='text-v3-black-placeholder' />}
                  onClick={onDelete}
                  size="sm"
                />
              </div>
            )}
            {showDeleteButton && isNumberType && (
              <div className={clsx(
                "flex items-center",
              )}>
                <ButtonIcon
                  icon={<DeleteIcon className='text-v3-black-placeholder' />}
                  onClick={onDelete}
                  size="lg"
                />
              </div>
            )}
          </div>

      </div>
      {/* Helper Text / Error Text */}
      {canHaveError && (
        <div
        className={clsx(
          'flex',
          'pt-v3-xxs',
          'pl-3',
          'items-start',
          'gap-[10px]',
          'min-h-[20px]'
        )}
      >
        {hasError ? (
          <span
            className={clsx(
              'text-v3-red-dark',
              'text-v3-h11',
              'font-normal',
              'not-italic'
            )}
          >
            {displayError}
          </span>
        ) : (
          <span
            className={clsx(
              'text-v3-black-secondary',
              'text-v3-h11',
              'font-normal',
              'italic'
            )}
          >
              {helperText}
            </span>
          )}
        </div>
      )}
    </div>
  )
}

export default Input